/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import styled from '@emotion/styled/macro'
import { connect } from 'react-redux'
import { useEffect, useMemo } from 'react'
import { getShootoutByGameId } from '@sportninja/common/selectors/shootout'
import { bindActionToPromise } from '@sportninja/common/actions/utils'
import shootoutActions from '@sportninja/common/actions/shootout'
import colors from '@sportninja/common/constants/appColors'
import Icon from '../../components/Icon'
import { paragraphSmall, weightSemiBold } from 'src/components/css'

const BoxScoreContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 100%;
`

const Header = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
`

const Title = styled.div`
  ${paragraphSmall}
  ${weightSemiBold}
  color: #c1c2c3;
  text-transform: uppercase;
  line-height: 16.8px;
  letter-spacing: 1.68px;
  word-wrap: break-word;
`

const ScoreCard = styled.div`
  padding: 16px;
  background: #27303e;
  border-radius: 8px;
  border: 1px solid #323a48;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 280px;
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 16px;
`

const TeamImageCell = styled.div`
  width: 32px;
  min-width: 32px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.7px;
`

const PeriodHeaderCell = styled.div`
  flex: 1;
  min-width: 0;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #c1c2c3;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  line-height: 20.3px;
`

const Divider = styled.div`
  height: 0;
  border: 1px solid #323a48;
  align-self: stretch;
`

const TeamRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 32px;
`

const ScoreCell = styled.div`
  flex: 1;
  min-width: 0;
  height: 24px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  line-height: 23.2px;
`

const ShootoutCard = ({ game, gameShootout, readShootout }) => {
  useEffect(() => {
    if (game.shootout) {
      readShootout(game.id)
    }
  }, [game.shootout, game.id, readShootout])

  const rounds = useMemo(() => {
    const shootout = []

    for (let i = 0; i < gameShootout?.data?.length; i = i + 2) {
      const firstShot = gameShootout?.data[i]
      const secondShot = gameShootout?.data[i + 1]
      const round = { id: `${firstShot?.id}-${secondShot?.id}` }

      if (firstShot?.home) {
        round.home = firstShot
        round.visitor = secondShot
      } else {
        round.visitor = firstShot
        round.home = secondShot
      }
      shootout.push(round)
    }

    return shootout
  }, [gameShootout?.data])

  if (!game.shootout || !gameShootout) {
    return null
  }

  return (
    <BoxScoreContainer>
      <Header>
        <Title>SHOOTOUT</Title>
      </Header>

      <ScoreCard>
        {/* Header Row */}
        <HeaderRow>
          <TeamImageCell> </TeamImageCell>
          {rounds.map((_, idx) => (
            <PeriodHeaderCell key={idx}>{idx + 1}</PeriodHeaderCell>
          ))}
        </HeaderRow>

        <Divider />

        {/* Home Team Row */}
        <TeamRow>
          <img
            src={
              game.homeTeam.imageId ||
              '/images/generic_placeholders/placeholder_team.png'
            }
            alt={game.homeTeam.name}
            css={css`
              width: 32px;
              height: 32px;
              object-fit: contain;
              background: transparent;
              border-radius: 8px;
            `}
            onError={(e) => {
              e.target.src = '/images/generic_placeholders/placeholder_team.png'
            }}
          />
          {rounds.map((round) => (
            <ScoreCell key={`home-${round.id}`}>
              <Icon
                name={round.home?.goal ? 'check' : 'times'}
                color={round.home?.goal ? colors.PRIMARY : colors.FIERY_RED}
              />
            </ScoreCell>
          ))}
        </TeamRow>

        {/* Visiting Team Row */}
        <TeamRow>
          <img
            src={
              game.visitingTeam.imageId ||
              '/images/generic_placeholders/placeholder_team.png'
            }
            alt={game.visitingTeam.name}
            css={css`
              width: 32px;
              height: 32px;
              object-fit: contain;
              background: transparent;
              border-radius: 8px;
            `}
            onError={(e) => {
              e.target.src = '/images/generic_placeholders/placeholder_team.png'
            }}
          />
          {rounds.map((round) => (
            <ScoreCell key={`visitor-${round.id}`}>
              <Icon
                name={round.visitor?.goal ? 'check' : 'times'}
                color={round.visitor?.goal ? colors.PRIMARY : colors.FIERY_RED}
              />
            </ScoreCell>
          ))}
        </TeamRow>
      </ScoreCard>
    </BoxScoreContainer>
  )
}

const mapStateToProps = (state, props) => ({
  gameShootout: getShootoutByGameId(state, props.game.id),
})

const mapDispatchToProps = (dispatch) => ({
  readShootout: bindActionToPromise(dispatch, shootoutActions.read.request),
})

export default connect(mapStateToProps, mapDispatchToProps)(ShootoutCard)
